/**
 * wall-art-format-static.js
 *
 * Per-format STATIC IMAGE swap for the product gallery's slot 6 (the second
 * static-detail slot, immediately after the slot-5 video). Mirrors
 * wall-art-format-video.js but for a plain <img> — no <deferred-media>. When the
 * customer changes format in the wall-art picker, the slot-6 main <img> + its
 * thumbnail <img> are swapped to the picked format's sitewide image, or the slot
 * + thumbnail are hidden (data-no-image, CSS-hidden) when that format has no
 * image uploaded.
 *
 * Data source: a sibling
 *   <script type="application/json" id="FormatStaticImageData-<section.id>">
 *     [ { "format": "art_print", "url": "...", "thumb_url": "...", "alt": "..." }, ... ]
 *   </script>
 * (emitted by snippets/product-media-gallery.liquid for each format that has an
 * image uploaded — empty array / missing tag = no images.)
 *
 * Trigger: `wallart:selection:changed` from assets/wall-art-picker.js with
 * `detail = { format, size, frameColor, variantId, price }`.
 */
(function () {
  'use strict';

  const SLIDE_SELECTOR     = '[data-format-image-slide]';
  const THUMB_SELECTOR     = '[data-format-image-thumbnail-slide]';
  const MAIN_IMG_SELECTOR  = '[data-format-image-main]';
  const THUMB_IMG_SELECTOR = '[data-format-image-thumbnail]';
  const DATA_SCRIPT_SELECTOR = 'script[type="application/json"][id^="FormatStaticImageData-"]';

  const controllers = new Map();

  function readData(sectionRoot) {
    const tag = sectionRoot.querySelector ? sectionRoot.querySelector(DATA_SCRIPT_SELECTOR) : null;
    if (!tag) return [];
    try {
      const parsed = JSON.parse(tag.textContent || 'null');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function findEntry(data, format) {
    if (!Array.isArray(data) || !format) return null;
    for (const e of data) {
      if (e && e.format === format) return e;
    }
    return null;
  }

  function findSectionRootForGallery(gallery) {
    return (
      gallery.closest('product-info') ||
      gallery.closest('[id^="ProductInfo-"]') ||
      gallery.closest('[id^="MainProduct-"]') ||
      document
    );
  }

  function setHidden(slide, thumb, hidden) {
    if (slide) {
      if (hidden) slide.setAttribute('data-no-image', '');
      else slide.removeAttribute('data-no-image');
    }
    if (thumb) {
      if (hidden) thumb.setAttribute('data-no-image', '');
      else thumb.removeAttribute('data-no-image');
    }
  }

  /** If slot 6 is the active slide but the new format has no image, click the
   *  first non-slot-6 thumbnail so the carousel jumps to a visible slide. */
  function jumpAway(thumbsContainer) {
    if (!thumbsContainer) return;
    const items = thumbsContainer.querySelectorAll('[data-target]');
    for (const item of items) {
      if (item.matches(THUMB_SELECTOR)) continue;
      const btn = item.querySelector('button');
      if (btn) { btn.click(); return; }
    }
  }

  function applySwap(sectionRoot, data, detail) {
    if (!detail) return;
    const gallery = sectionRoot.querySelector ? sectionRoot.querySelector('media-gallery') : null;
    if (!gallery) return;

    const slide = gallery.querySelector(SLIDE_SELECTOR);
    const thumbsContainer = gallery.querySelector('[id^="GalleryThumbnails-"]');
    const thumb = thumbsContainer ? thumbsContainer.querySelector(THUMB_SELECTOR) : null;
    const entry = findEntry(data, detail.format);
    const isActive = !!(slide && slide.classList.contains('is-active'));

    if (!entry) {
      setHidden(slide, thumb, true);
      if (isActive) jumpAway(thumbsContainer);
      return;
    }

    setHidden(slide, thumb, false);

    const mainImg = slide ? slide.querySelector(MAIN_IMG_SELECTOR) : null;
    const thumbImg = thumb ? thumb.querySelector(THUMB_IMG_SELECTOR) : null;

    if (mainImg) {
      if (entry.url) mainImg.setAttribute('src', entry.url);
      if (entry.alt) mainImg.setAttribute('alt', entry.alt);
      mainImg.removeAttribute('srcset');
    }
    if (thumbImg) {
      if (entry.thumb_url) thumbImg.setAttribute('src', entry.thumb_url);
      if (entry.alt) thumbImg.setAttribute('alt', entry.alt);
      thumbImg.removeAttribute('srcset');
    }
  }

  function initForGallery(gallery) {
    const sectionRoot = findSectionRootForGallery(gallery);
    const sectionId = String(gallery.id || '').replace(/^MediaGallery-/, '');
    if (!sectionId) return;

    if (controllers.has(sectionId)) {
      try { controllers.get(sectionId).abort(); } catch { /* ignore */ }
      controllers.delete(sectionId);
    }

    const data = readData(sectionRoot);
    const abortController = new AbortController();
    controllers.set(sectionId, abortController);

    const onChange = (event) => applySwap(sectionRoot, data, event && event.detail);

    sectionRoot.addEventListener('wallart:selection:changed', onChange, { signal: abortController.signal });
    document.addEventListener('wallart:selection:changed', onChange, { signal: abortController.signal });

    if (window.__wallArtCurrentSelection) {
      applySwap(sectionRoot, data, window.__wallArtCurrentSelection);
    }
  }

  function initAll(root) {
    const scope = (root && root.querySelectorAll) ? root : document;
    if (root && root.matches && root.matches('media-gallery')) initForGallery(root);
    scope.querySelectorAll('media-gallery').forEach((g) => initForGallery(g));
  }

  function destroySection(sectionId) {
    const ctrl = controllers.get(sectionId);
    if (!ctrl) return;
    try { ctrl.abort(); } catch { /* ignore */ }
    controllers.delete(sectionId);
  }

  function start() {
    initAll(document);

    document.addEventListener('shopify:section:load', (event) => {
      const target = event && event.target instanceof Element ? event.target : document;
      initAll(target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const detailSectionId = String((event && event.detail && event.detail.sectionId) || '').trim();
      if (detailSectionId) { destroySection(detailSectionId); return; }
      const target = event && event.target instanceof Element ? event.target : null;
      if (!target) return;
      const gallery = (target.matches && target.matches('media-gallery'))
        ? target
        : (target.querySelector && target.querySelector('media-gallery'));
      if (!gallery) return;
      const derived = String(gallery.id || '').replace(/^MediaGallery-/, '');
      if (derived) destroySection(derived);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
